import { useCallback } from 'react'
import * as Styled from '../../styled'
import {
  MAX_FONT_SIZE,
  MAX_STROKE_THICKNESS,
  MIN_FONT_SIZE,
  MIN_STROKE_THICKNESS
} from 'consts/text'
import { Text, NumericStepper, ColorPicker } from 'common-components'
import { ITextData } from 'types/media'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { dispatch } from 'store'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { clampStrokeThickness, scaleProportional } from 'utils/text'

const generateSizePresets = (
  min: number,
  max: number,
  step: number
): number[] => {
  const presets: number[] = []
  for (let v = min; v <= max; v += step) {
    presets.push(v)
    if (v >= 40) step = 8
    else if (v >= 24) step = 4
  }
  return presets
}

const FONT_SIZE_PRESETS = generateSizePresets(MIN_FONT_SIZE, MAX_FONT_SIZE, 8)
const STROKE_THICKNESS_PRESETS = generateSizePresets(
  MIN_STROKE_THICKNESS,
  MAX_STROKE_THICKNESS,
  1
)

const pxPresetLabel = (v: number) => `${v}px`

interface IStylesContainerProps {
  persistTextData: (
    patch: Partial<ITextData>,
    sizeOverrides?: { width: number; height: number }
  ) => void
  originalId: string
  textData: ITextData
}

export const StylesContainer = (props: IStylesContainerProps) => {
  const { persistTextData, originalId, textData } = props

  const handleFontSizeChange = useCallback(
    (newFontSize: number) => {
      const { fontSize: oldFontSize, strokeThickness: oldStroke } = textData
      const newStroke = scaleProportional(
        oldFontSize,
        newFontSize,
        oldStroke,
        clampStrokeThickness
      )
      dispatch(
        projectDataThunks.updateTextSizeParams(
          originalId,
          newFontSize,
          newStroke
        )
      )
    },
    [originalId, textData]
  )

  const handleStrokeChange = useCallback(
    (newStroke: number) => {
      dispatch(
        projectDataThunks.updateTextSizeParams(
          originalId,
          textData.fontSize,
          newStroke
        )
      )
    },
    [originalId, textData.fontSize]
  )

  return (
    <>
      <Styled.Row>
        <Styled.Field>
          <Text type="subtitle">{translate(T.TEXT_COLOR_LABEL)}</Text>
          <ColorPicker
            triggerTestId="text-overlay-text-color-picker-trigger"
            value={textData.fillColor}
            onChangeComplete={(hex: string) => {
              persistTextData({ fillColor: hex })
            }}
          />
        </Styled.Field>
        <Styled.Field>
          <Text type="subtitle">{translate(T.TEXT_SIZE_LABEL)}</Text>
          <NumericStepper
            decrementTestId="text-overlay-font-size-decrement"
            incrementTestId="text-overlay-font-size-increment"
            value={Math.round(textData.fontSize)}
            onChange={handleFontSizeChange}
            min={MIN_FONT_SIZE}
            max={MAX_FONT_SIZE}
            presets={FONT_SIZE_PRESETS}
            formatLabel={pxPresetLabel}
          />
        </Styled.Field>
      </Styled.Row>
      <Styled.Row>
        <Styled.Field>
          <Text type="subtitle">{translate(T.STROKE_COLOR_LABEL)}</Text>
          <ColorPicker
            triggerTestId="text-overlay-stroke-color-picker-trigger"
            value={textData.strokeColor}
            onChangeComplete={(hex: string) => {
              persistTextData({ strokeColor: hex })
            }}
          />
        </Styled.Field>
        <Styled.Field>
          <Text type="subtitle">{translate(T.STROKE_WEIGHT_LABEL)}</Text>
          <NumericStepper
            decrementTestId="text-overlay-stroke-thickness-decrement"
            incrementTestId="text-overlay-stroke-thickness-increment"
            value={Math.round(textData.strokeThickness)}
            onChange={handleStrokeChange}
            min={MIN_STROKE_THICKNESS}
            max={MAX_STROKE_THICKNESS}
            presets={STROKE_THICKNESS_PRESETS}
          />
        </Styled.Field>
      </Styled.Row>
    </>
  )
}
